import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  CreateGeoEventProvisionalSubmissionResponse,
  CreateReportProvisionalSubmissionResponse,
  GeoEventCreateUpdateData,
  ProvisionalSubmissionResponse,
  ProvisionalSubmissionType,
} from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { PostReportData } from 'features/reports/report.types';
import { invalidateGeoEventQueries } from 'features/geoEvents/hooks/useGeoEvent';
import {
  invalidateReportsQueryCache,
  getRedundancyMessage as getReportRedundancyMessage,
} from 'features/reports/components/ReportForm/utils';
import { HttpStatusCode, isAxiosError } from 'axios';
import { getRedundancyMessage as getGeoEventRedundancyMessage } from 'features/geoEvents/geoEvent.utils';
import { useConfirm } from 'components/ConfirmDialog';
import {
  approveProvisionalSubmission,
  postProvisionalSubmission,
  rejectProvisionalSubmission,
} from './useProvisionalSubmissionMutations.queries';

type ApprovedGeoEventResponse = CreateGeoEventProvisionalSubmissionResponse & {
  geoEventId: number;
};

const assertHasGeoEventId = (
  response: CreateGeoEventProvisionalSubmissionResponse,
): ApprovedGeoEventResponse => {
  if (response.geoEventId === undefined) {
    throw new Error(
      'Provisional geo event approval response missing geoEventId',
    );
  }
  return response as ApprovedGeoEventResponse;
};

// Create submissions are checked for redundancy against nearby incidents: the
// server responds 409, the user is asked to confirm, and a `null` resolution
// means they declined and nothing was submitted. Edits to an existing incident
// cannot be redundant, so the edit variants skip the check and never resolve
// to null.
type UseProvisionalSubmissionMutations = {
  submitProvisionalGeoEventCreate: (
    data: GeoEventCreateUpdateData,
  ) => Promise<ProvisionalSubmissionResponse | null>;
  submitProvisionalGeoEventEdit: (
    data: GeoEventCreateUpdateData,
    geoEventId: number,
  ) => Promise<ProvisionalSubmissionResponse>;
  submitProvisionalReport: (
    data: PostReportData,
  ) => Promise<ProvisionalSubmissionResponse | null>;
  approveProvisionalGeoEventCreate: (
    id: number,
    approvedData: GeoEventCreateUpdateData,
  ) => Promise<ApprovedGeoEventResponse | null>;
  approveProvisionalGeoEventEdit: (
    id: number,
    approvedData: GeoEventCreateUpdateData,
    geoEventId: number,
  ) => Promise<ApprovedGeoEventResponse>;
  approveProvisionalReport: (
    id: number,
    approvedData: PostReportData,
  ) => Promise<CreateReportProvisionalSubmissionResponse | null>;
  rejectProvisionalSubmission: (
    id: number,
  ) => Promise<ProvisionalSubmissionResponse>;
  isRejecting: boolean;
};

export const useProvisionalSubmissionMutations =
  (): UseProvisionalSubmissionMutations => {
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    const withRedundancyCheck = async <T>(
      action: (skipRedundancyCheck?: boolean) => Promise<T>,
      getRedundancyMessage: (response: unknown) => string[],
    ): Promise<T | null> => {
      try {
        return await action();
      } catch (error: unknown) {
        if (!isAxiosError(error)) throw error;
        if (error.response?.status === HttpStatusCode.Conflict) {
          const confirmed = await confirm(getRedundancyMessage(error.response));
          if (confirmed) return action(true);
          return null;
        }
        throw error;
      }
    };

    const createMutation = useMutation({
      mutationFn: postProvisionalSubmission,
    });

    const approveMutation = useMutation({
      mutationFn: ({
        id,
        approvedData,
        skipRedundancyCheck,
      }: {
        id: number;
        approvedData: GeoEventCreateUpdateData;
        skipRedundancyCheck?: boolean;
      }) =>
        approveProvisionalSubmission<
          CreateGeoEventProvisionalSubmissionResponse,
          GeoEventCreateUpdateData
        >(id, approvedData, skipRedundancyCheck),
      onSettled: async (data, _error, { id, approvedData }) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['provisionalSubmission', id],
            refetchType: 'none',
          }),
          invalidateGeoEventQueries(
            queryClient,
            data?.geoEventId ?? approvedData.id,
          ),
        ]);
      },
    });

    const approveReportMutation = useMutation({
      mutationFn: ({
        id,
        approvedData,
        skipRedundancyCheck,
      }: {
        id: number;
        approvedData: PostReportData;
        skipRedundancyCheck?: boolean;
      }) =>
        approveProvisionalSubmission<
          CreateReportProvisionalSubmissionResponse,
          PostReportData
        >(id, approvedData, skipRedundancyCheck),
      onSettled: async (_data, _error, { id, approvedData }) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['provisionalSubmission', id],
            refetchType: 'none',
          }),
          invalidateReportsQueryCache(queryClient, approvedData.geoEventId!),
          invalidateGeoEventQueries(queryClient, approvedData.geoEventId!),
        ]);
      },
    });

    const rejectMutation = useMutation({
      mutationFn: rejectProvisionalSubmission,
      onSettled: (_data, _error, id) =>
        queryClient.invalidateQueries({
          queryKey: ['provisionalSubmission', id],
          refetchType: 'none',
        }),
    });

    const submitProvisionalGeoEventCreate = (
      data: GeoEventCreateUpdateData,
    ): Promise<ProvisionalSubmissionResponse | null> =>
      withRedundancyCheck(
        (skipRedundancyCheck) =>
          createMutation.mutateAsync({
            submissionType: ProvisionalSubmissionType.CreateGeoEvent,
            submittedData: data,
            skipRedundancyCheck,
          }),
        getGeoEventRedundancyMessage,
      );

    const submitProvisionalGeoEventEdit = (
      data: GeoEventCreateUpdateData,
      geoEventId: number,
    ): Promise<ProvisionalSubmissionResponse> =>
      createMutation.mutateAsync({
        submissionType: ProvisionalSubmissionType.EditGeoEvent,
        submittedData: { ...data, id: geoEventId },
      });

    const submitProvisionalReport = (
      data: PostReportData,
    ): Promise<ProvisionalSubmissionResponse | null> =>
      withRedundancyCheck(
        (skipRedundancyCheck) =>
          createMutation.mutateAsync({
            submissionType: ProvisionalSubmissionType.CreateReport,
            submittedData: data,
            skipRedundancyCheck,
          }),
        getReportRedundancyMessage,
      );

    const approveProvisionalGeoEventCreate = async (
      id: number,
      approvedData: GeoEventCreateUpdateData,
    ): Promise<ApprovedGeoEventResponse | null> => {
      const response = await withRedundancyCheck(
        (skipRedundancyCheck) =>
          approveMutation.mutateAsync({
            id,
            approvedData,
            skipRedundancyCheck,
          }),
        getGeoEventRedundancyMessage,
      );
      return response ? assertHasGeoEventId(response) : null;
    };

    const approveProvisionalGeoEventEdit = async (
      id: number,
      approvedData: GeoEventCreateUpdateData,
      geoEventId: number,
    ): Promise<ApprovedGeoEventResponse> => {
      const response = await approveMutation.mutateAsync({
        id,
        approvedData: { ...approvedData, id: geoEventId },
      });
      return assertHasGeoEventId(response);
    };

    const approveProvisionalReport = (
      id: number,
      approvedData: PostReportData,
    ): Promise<CreateReportProvisionalSubmissionResponse | null> =>
      withRedundancyCheck(
        (skipRedundancyCheck) =>
          approveReportMutation.mutateAsync({
            id,
            approvedData,
            skipRedundancyCheck,
          }),
        getReportRedundancyMessage,
      );

    return {
      submitProvisionalGeoEventCreate,
      submitProvisionalGeoEventEdit,
      submitProvisionalReport,
      approveProvisionalGeoEventCreate,
      approveProvisionalGeoEventEdit,
      approveProvisionalReport,
      rejectProvisionalSubmission: rejectMutation.mutateAsync,
      isRejecting: rejectMutation.isPending,
    };
  };
